import { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { fmtMoney } from '../lib/format';
import toast from 'react-hot-toast';
import {
  Robot,
  House,
  Buildings,
  Gear,
  SignOut,
  ChartLineUp,
  List,
  X,
  CurrencyDollar,
  Users,
  ChatCircle,
  Lightning,
  ArrowLeft,
  MagnifyingGlass,
  PencilSimple,
  ShieldCheck
} from '@phosphor-icons/react';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/admin', icon: House, label: 'Dashboard', exact: true },
    { path: '/admin/companies', icon: Buildings, label: 'Empresas' },
    { path: '/admin/transactions', icon: CurrencyDollar, label: 'Movimientos' },
    { path: '/admin/agents', icon: Robot, label: 'Agentes' },
    { path: '/admin/channels', icon: ChatCircle, label: 'Canales' },
    { path: '/admin/settings', icon: Gear, label: 'Configuración' },
  ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#09090B]/80 backdrop-blur-xl border-b border-[#27272A]/60 z-40 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-white">
          <List size={24} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/images/novagente-logo.png" alt="Novagente" className="h-6 brightness-0 invert" />
          <span className="font-bold text-white text-sm">ADMIN</span>
        </div>
        <div className="w-10" />
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#09090B] border-r border-[#27272A] z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          <div className="h-16 flex items-center justify-between px-6 border-b border-[#27272A]">
            <div className="flex items-center gap-2">
              <img src="/images/novagente-logo.png" alt="Novagente" className="h-7 brightness-0 invert" />
              <span className="font-bold text-white text-sm">ADMIN</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white">
              <X size={20} />
            </button>
          </div>

          <nav className="flex-1 py-4 px-3 overflow-y-auto space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${
                  isActive(item)
                    ? 'bg-[#0047FF]/20 text-white font-semibold'
                    : 'text-[#A1A1AA] hover:bg-[#27272A] hover:text-white'
                }`}
              >
                <item.icon size={20} weight={isActive(item) ? 'fill' : 'regular'} />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="border-t border-[#27272A] p-4">
            <Link
              to="/dashboard"
              data-testid="back-to-dashboard"
              className="flex items-center justify-center gap-2 w-full mb-4 py-2.5 text-sm font-medium text-white bg-[#27272A] border border-[#3F3F46] rounded-xl hover:border-[#0047FF] hover:text-[#0047FF] transition-colors"
            >
              <ArrowLeft size={16} />
              Volver al dashboard
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0047FF] text-white rounded-xl flex items-center justify-center font-bold">
                A
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">Admin</p>
                <p className="text-xs text-[#A1A1AA] truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-red-500 transition-colors"
            >
              <SignOut size={18} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

// Admin Dashboard
export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.adminGetStats();
      setStats(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-white">
          Panel de Administración
        </h1>
        <p className="text-[#A1A1AA]">Métricas globales de la plataforma</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Buildings size={24} className="text-[#0047FF]" />
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Empresas</p>
          </div>
          <p className="font-black text-3xl text-white">{stats?.total_companies || 0}</p>
        </div>
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Lightning size={24} className="text-green-500" />
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Suscripciones activas</p>
          </div>
          <p className="font-black text-3xl text-white">{stats?.active_subscriptions || 0}</p>
        </div>
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <CurrencyDollar size={24} className="text-yellow-500" />
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">MRR</p>
          </div>
          <p className="font-black text-3xl text-white">${fmtMoney(stats?.mrr)}</p>
        </div>
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Robot size={24} className="text-purple-500" />
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Total agentes</p>
          </div>
          <p className="font-black text-3xl text-white">{stats?.total_agents || 0}</p>
        </div>
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <ChatCircle size={24} className="text-cyan-500" />
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Conversaciones</p>
          </div>
          <p className="font-black text-3xl text-white">{stats?.total_conversations || 0}</p>
        </div>
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users size={24} className="text-pink-500" />
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Mensajes</p>
          </div>
          <p className="font-black text-3xl text-white">{stats?.total_messages || 0}</p>
        </div>
      </div>
    </div>
  );
}

// Admin Companies
const ADMIN_PLANS = [
  { id: 'free', name: 'Gratis', price: 0 },
  { id: 'starter', name: 'Starter', price: 199 },
  { id: 'pro', name: 'Pro', price: 499 },
  { id: 'business', name: 'Business', price: 999 },
];

export function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceModal, setBalanceModal] = useState(null); // company being credited
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [planModal, setPlanModal] = useState(null); // company whose plan is being changed
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planReason, setPlanReason] = useState('');
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const { user: currentUser } = useAuth();
  const [detailModal, setDetailModal] = useState(null); // {company, users, stats}
  const [detailLoading, setDetailLoading] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState(null); // user id being toggled

  const filteredCompanies = companies.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.owner_email?.toLowerCase().includes(search.toLowerCase())
  );

  const openDetailModal = async (company) => {
    setDetailLoading(true);
    setDetailModal({ company, users: [], stats: null });
    try {
      const res = await api.adminGetCompanyDetail(company.id);
      setDetailModal(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar la empresa');
      setDetailModal(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleAdmin = async (u) => {
    const newRole = u.role === 'admin' ? 'client' : 'admin';
    setRoleUpdating(u.id);
    try {
      await api.adminSetUserRole(u.id, newRole);
      setDetailModal((prev) => prev ? {
        ...prev,
        users: prev.users.map((x) => (x.id === u.id ? { ...x, role: newRole } : x)),
      } : prev);
      toast.success(newRole === 'admin'
        ? `${u.name || u.email} ahora es admin de la plataforma`
        : `Privilegios de admin retirados a ${u.name || u.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar el rol');
    } finally {
      setRoleUpdating(null);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await api.adminGetCompanies();
      // Newest registrations first
      setCompanies([...res.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openBalanceModal = (company) => {
    setBalanceModal(company);
    setAmount('');
    setReason('');
    setConfirmStep(false);
  };

  const closeBalanceModal = () => {
    setBalanceModal(null);
    setAmount('');
    setReason('');
    setConfirmStep(false);
  };

  const goToConfirm = () => {
    const parsed = parseFloat(amount);
    if (!parsed || isNaN(parsed)) {
      toast.error('Ingresa un monto válido (puede ser negativo para descontar)');
      return;
    }
    setConfirmStep(true);
  };

  const openPlanModal = (company) => {
    setPlanModal(company);
    setSelectedPlan(company.plan || 'free');
    setPlanReason('');
  };

  const closePlanModal = () => {
    setPlanModal(null);
    setSelectedPlan('');
    setPlanReason('');
  };

  const handleSetPlan = async () => {
    if (!selectedPlan || selectedPlan === planModal.plan) {
      toast.error('Selecciona un plan distinto al actual');
      return;
    }
    setPlanSubmitting(true);
    try {
      const res = await api.adminSetPlan(planModal.id, selectedPlan, planReason.trim() || null);
      setCompanies((prev) => prev.map((c) =>
        c.id === planModal.id
          ? { ...c, plan: selectedPlan, subscription_status: selectedPlan === 'free' ? 'trial' : 'active' }
          : c
      ));
      toast.success(`Plan de ${planModal.name} cambiado a ${ADMIN_PLANS.find(p => p.id === selectedPlan)?.name}`);
      if (res.data.warning) toast(res.data.warning, { icon: '⚠️', duration: 6000 });
      closePlanModal();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al cambiar el plan');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleAddBalance = async () => {
    const parsed = parseFloat(amount);
    setSubmitting(true);
    try {
      const res = await api.adminAddBalance(balanceModal.id, parsed, reason.trim() || null);
      setCompanies((prev) => prev.map((c) =>
        c.id === balanceModal.id ? { ...c, balance_mxn: res.data.new_balance } : c
      ));
      toast.success(
        parsed > 0
          ? `Se agregaron $${fmtMoney(parsed)} MXN a ${balanceModal.name}`
          : `Se descontaron $${fmtMoney(Math.abs(parsed))} MXN de ${balanceModal.name}`
      );
      closeBalanceModal();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al actualizar el saldo');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-white">
            Empresas Registradas
          </h1>
          <p className="text-[#A1A1AA]">
            {search ? `${filteredCompanies.length} de ${companies.length}` : `${companies.length}`} empresas · más recientes primero
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o correo..."
            data-testid="company-search"
            className="w-full pl-10 pr-4 py-2.5 bg-[#27272A] border border-[#3F3F46] rounded-xl text-white text-sm placeholder-[#52525B] focus:border-[#0047FF] focus:outline-none"
          />
        </div>
      </div>

      <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#3F3F46]">
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Empresa</th>
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Email</th>
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Plan</th>
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Estado</th>
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Agentes</th>
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Mensajes del bot</th>
                <th className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Saldo MXN</th>
                <th className="px-6 py-4 text-right font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => (
                <tr key={company.id} className="border-b border-[#3F3F46] last:border-b-0" data-testid={`company-row-${company.id}`}>
                  <td className="px-6 py-4">
                    <p className="font-medium text-white">{company.name}</p>
                    <p className="text-xs text-[#A1A1AA]">{new Date(company.created_at).toLocaleDateString('es-MX')}</p>
                  </td>
                  <td className="px-6 py-4 text-[#A1A1AA]">{company.owner_email}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-[#3F3F46] text-white capitalize">
                      {company.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium ${
                      company.subscription_status === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {company.subscription_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white">{company.agents_count}</td>
                  <td className="px-6 py-4 text-[#A1A1AA]">
                    {company.token_usage} / {company.token_limit}
                  </td>
                  <td className="px-6 py-4 text-white font-mono">
                    ${fmtMoney(company.balance_mxn)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => openDetailModal(company)}
                        className="px-3 py-1.5 text-xs font-medium border border-[#3F3F46] text-white rounded-xl hover:border-[#0047FF] hover:text-[#0047FF] inline-flex items-center gap-1"
                        data-testid={`edit-company-${company.id}`}
                      >
                        <PencilSimple size={14} /> Editar
                      </button>
                      <button
                        onClick={() => openPlanModal(company)}
                        className="px-3 py-1.5 text-xs font-medium bg-[#3F3F46] text-white rounded-xl hover:bg-[#52525B] inline-flex items-center gap-1"
                        data-testid={`set-plan-${company.id}`}
                      >
                        <Lightning size={14} /> Plan
                      </button>
                      <button
                        onClick={() => openBalanceModal(company)}
                        className="px-3 py-1.5 text-xs font-medium bg-[#0047FF] text-white rounded-xl hover:bg-[#0035CC] inline-flex items-center gap-1"
                        data-testid={`add-balance-${company.id}`}
                      >
                        <CurrencyDollar size={14} /> Saldo
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredCompanies.length === 0 && (
            <p className="p-8 text-center text-[#A1A1AA] text-sm">
              {search ? `Sin resultados para "${search}"` : 'No hay empresas registradas'}
            </p>
          )}
        </div>
      </div>

      {detailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#3F3F46] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">{detailModal.company?.name}</h3>
                <p className="text-xs text-[#A1A1AA] mt-1">{detailModal.company?.owner_email}</p>
              </div>
              <button onClick={() => setDetailModal(null)} className="text-[#A1A1AA] hover:text-white">
                <X size={20} />
              </button>
            </div>

            {detailLoading ? (
              <div className="p-12 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Company info */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Plan', value: detailModal.company?.plan, cap: true },
                    { label: 'Estado', value: detailModal.company?.subscription_status },
                    { label: 'Saldo', value: `$${fmtMoney(detailModal.company?.balance_mxn)} MXN` },
                    { label: 'Mensajes bot', value: `${detailModal.company?.token_usage ?? 0} / ${detailModal.company?.token_limit ?? 0}` },
                    { label: 'Creada', value: detailModal.company?.created_at ? new Date(detailModal.company.created_at).toLocaleDateString('es-MX') : '—' },
                    { label: 'Vence plan', value: detailModal.company?.plan_expires_at ? new Date(detailModal.company.plan_expires_at).toLocaleDateString('es-MX') : '—' },
                  ].map((f) => (
                    <div key={f.label} className="bg-[#09090B] border border-[#3F3F46] rounded-xl p-3">
                      <p className="text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA]">{f.label}</p>
                      <p className={`text-sm text-white font-medium mt-0.5 ${f.cap ? 'capitalize' : ''}`}>{f.value || '—'}</p>
                    </div>
                  ))}
                </div>

                {/* Usage stats */}
                {detailModal.stats && (
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: 'Agentes', value: detailModal.stats.agents },
                      { label: 'Canales', value: detailModal.stats.channels },
                      { label: 'Conversaciones', value: detailModal.stats.conversations },
                      { label: 'Contactos', value: detailModal.stats.contacts },
                    ].map((s) => (
                      <div key={s.label} className="bg-[#09090B] border border-[#3F3F46] rounded-xl p-3 text-center">
                        <p className="font-black text-xl text-white">{s.value}</p>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA]">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Users & admin privileges */}
                <div>
                  <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-3">Usuarios de la empresa</p>
                  <div className="space-y-2">
                    {detailModal.users?.map((u) => (
                      <div key={u.id} className="flex items-center gap-3 bg-[#09090B] border border-[#3F3F46] rounded-xl p-3">
                        <div className="w-9 h-9 rounded-xl bg-[#0047FF]/20 text-[#60A5FA] flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {(u.name || u.email)[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium truncate">
                            {u.name || u.email}
                            {u.id === currentUser?.id && <span className="ml-1 text-xs text-[#A1A1AA]">(tú)</span>}
                          </p>
                          <p className="text-xs text-[#A1A1AA] truncate">{u.email}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full flex-shrink-0 ${
                          u.company_role === 'agent' ? 'bg-green-500/20 text-green-400' : 'bg-[#3F3F46] text-white'
                        }`}>
                          {u.company_role === 'agent' ? 'Agente' : 'Dueño'}
                        </span>
                        {u.role === 'admin' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-purple-500/20 text-purple-300 inline-flex items-center gap-1 flex-shrink-0">
                            <ShieldCheck size={11} /> ADMIN
                          </span>
                        )}
                        <button
                          onClick={() => handleToggleAdmin(u)}
                          disabled={roleUpdating === u.id || (u.id === currentUser?.id && u.role === 'admin')}
                          data-testid={`toggle-admin-${u.id}`}
                          title={u.id === currentUser?.id && u.role === 'admin' ? 'No puedes quitarte tu propio admin' : ''}
                          className={`px-3 py-1.5 text-xs font-medium rounded-xl flex-shrink-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            u.role === 'admin'
                              ? 'border border-red-500/40 text-red-400 hover:bg-red-500/10'
                              : 'border border-[#0047FF]/50 text-[#60A5FA] hover:bg-[#0047FF]/10'
                          }`}
                        >
                          {roleUpdating === u.id ? '...' : u.role === 'admin' ? 'Quitar admin' : 'Hacer admin'}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#52525B] mt-2">
                    Un admin de plataforma tiene acceso a este panel y a todas las empresas. El cambio aplica de inmediato.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {planModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b border-[#3F3F46] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">Cambiar plan</h3>
                <p className="text-xs text-[#A1A1AA] mt-1">{planModal.name} · plan actual: <span className="capitalize">{planModal.plan}</span></p>
              </div>
              <button onClick={closePlanModal} className="text-[#A1A1AA] hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {ADMIN_PLANS.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`p-3 border text-left transition-colors ${
                      selectedPlan === plan.id
                        ? 'border-[#0047FF] bg-[#0047FF]/10'
                        : 'border-[#3F3F46] hover:border-[#52525B]'
                    }`}
                    data-testid={`plan-option-${plan.id}`}
                  >
                    <p className={`font-bold text-sm ${selectedPlan === plan.id ? 'text-[#0047FF]' : 'text-white'}`}>
                      {plan.name}
                      {planModal.plan === plan.id && <span className="ml-1 text-xs font-normal text-[#A1A1AA]">(actual)</span>}
                    </p>
                    <p className="text-xs text-[#A1A1AA] font-mono">${plan.price}/mes</p>
                  </button>
                ))}
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Motivo <span className="text-[#A1A1AA] normal-case">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={planReason}
                  onChange={(e) => setPlanReason(e.target.value)}
                  placeholder="Ej: Cortesía, cliente especial, ajuste comercial..."
                  maxLength={120}
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
              </div>

              <p className="text-xs text-[#A1A1AA]">
                El cambio es inmediato y no pasa por Stripe. Si la empresa tiene una suscripción de Stripe activa, no se cancela automáticamente.
              </p>
            </div>

            <div className="p-6 border-t border-[#3F3F46] flex gap-3">
              <button
                onClick={closePlanModal}
                disabled={planSubmitting}
                className="flex-1 py-2 border border-[#3F3F46] rounded-xl text-white font-medium hover:bg-[#3F3F46] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSetPlan}
                disabled={planSubmitting || !selectedPlan || selectedPlan === planModal.plan}
                className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
                data-testid="plan-confirm"
              >
                {planSubmitting ? 'Aplicando...' : 'Cambiar plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {balanceModal && (() => {
        const parsed = parseFloat(amount) || 0;
        const isCredit = parsed > 0;
        const currentBalance = balanceModal.balance_mxn ?? 0;
        const newBalance = currentBalance + parsed;
        return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b border-[#3F3F46] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">
                  {confirmStep ? 'Confirmar ajuste de saldo' : 'Ajustar saldo'}
                </h3>
                <p className="text-xs text-[#A1A1AA] mt-1">{balanceModal.name}</p>
              </div>
              <button onClick={closeBalanceModal} className="text-[#A1A1AA] hover:text-white">
                <X size={20} />
              </button>
            </div>

            {!confirmStep ? (
              <>
                <div className="p-6 space-y-4">
                  <div className="bg-[#09090B] border border-[#3F3F46] rounded-xl p-3">
                    <p className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-1">Saldo actual</p>
                    <p className="text-2xl font-bold text-white font-mono">
                      ${fmtMoney(currentBalance)} <span className="text-sm text-[#A1A1AA]">MXN</span>
                    </p>
                  </div>

                  <div>
                    <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                      Monto MXN <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="100.00"
                      className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none font-mono"
                      data-testid="balance-amount-input"
                    />
                    <p className="text-xs text-[#A1A1AA] mt-1">
                      Usa monto positivo para acreditar, negativo para descontar.
                    </p>
                  </div>

                  <div>
                    <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                      Motivo <span className="text-[#A1A1AA] normal-case">(opcional)</span>
                    </label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Ej: Recarga manual, ajuste por incidente, etc."
                      maxLength={120}
                      className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="p-6 border-t border-[#3F3F46] flex gap-3">
                  <button
                    onClick={closeBalanceModal}
                    className="flex-1 py-2 border border-[#3F3F46] rounded-xl text-white font-medium hover:bg-[#3F3F46]"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={goToConfirm}
                    disabled={!amount}
                    className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
                    data-testid="balance-submit"
                  >
                    Continuar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-6 space-y-4">
                  <div className={`p-4 border ${isCredit ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <p className={`text-xs uppercase tracking-wider mb-2 ${isCredit ? 'text-green-400' : 'text-red-400'}`}>
                      {isCredit ? 'Vas a acreditar' : 'Vas a descontar'}
                    </p>
                    <p className={`text-3xl font-bold font-mono ${isCredit ? 'text-green-400' : 'text-red-400'}`}>
                      {isCredit ? '+' : '−'} ${fmtMoney(Math.abs(parsed))} <span className="text-base text-[#A1A1AA]">MXN</span>
                    </p>
                  </div>

                  <div className="bg-[#09090B] border border-[#3F3F46] rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">Saldo actual</span>
                      <span className="text-white font-mono">${fmtMoney(currentBalance)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">Ajuste</span>
                      <span className={`font-mono ${isCredit ? 'text-green-400' : 'text-red-400'}`}>
                        {isCredit ? '+' : ''}{fmtMoney(parsed)}
                      </span>
                    </div>
                    <div className="border-t border-[#3F3F46] pt-3 flex justify-between items-center">
                      <span className="text-xs uppercase tracking-wider text-white font-bold">Saldo final</span>
                      <span className="text-white font-mono text-lg font-bold">${fmtMoney(newBalance)}</span>
                    </div>
                  </div>

                  {reason.trim() && (
                    <div className="bg-[#09090B] border border-[#3F3F46] rounded-xl p-3">
                      <p className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-1">Motivo</p>
                      <p className="text-sm text-white">{reason.trim()}</p>
                    </div>
                  )}

                  <div className="bg-[#09090B] border border-[#3F3F46] rounded-xl p-3">
                    <p className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-1">Empresa</p>
                    <p className="text-sm text-white font-medium">{balanceModal.name}</p>
                    <p className="text-xs text-[#A1A1AA]">{balanceModal.owner_email}</p>
                  </div>
                </div>
                <div className="p-6 border-t border-[#3F3F46] flex gap-3">
                  <button
                    onClick={() => setConfirmStep(false)}
                    disabled={submitting}
                    className="flex-1 py-2 border border-[#3F3F46] rounded-xl text-white font-medium hover:bg-[#3F3F46] disabled:opacity-50"
                  >
                    Volver
                  </button>
                  <button
                    onClick={handleAddBalance}
                    disabled={submitting}
                    className={`flex-1 py-2 rounded-xl text-white font-medium disabled:opacity-50 ${
                      isCredit ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                    }`}
                    data-testid="balance-confirm"
                  >
                    {submitting ? 'Aplicando...' : (isCredit ? 'Acreditar saldo' : 'Descontar saldo')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// Shared table shell for admin list pages
function AdminTable({ headers, children }) {
  return (
    <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#3F3F46]">
              {headers.map((h) => (
                <th key={h} className="px-6 py-4 text-left font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

const TX_TYPE_LABELS = {
  credit: 'Crédito', topup: 'Recarga', admin_credit: 'Crédito admin', admin_debit: 'Cargo admin',
  subscription: 'Suscripción', plan_purchase: 'Compra de plan', number_purchase: 'Compra de número',
  admin_plan_change: 'Cambio de plan',
};
const TX_POSITIVE = new Set(['credit', 'topup', 'admin_credit']);

// Admin Transactions (global)
export function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.adminGetTransactions();
        setTransactions(res.data.transactions || []);
      } catch {
        toast.error('Error al cargar movimientos');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = transactions.filter((t) =>
    t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-white">Movimientos</h1>
          <p className="text-[#A1A1AA]">Transacciones de todas las empresas · más recientes primero</p>
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por empresa o concepto..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#27272A] border border-[#3F3F46] rounded-xl text-white text-sm placeholder-[#52525B] focus:border-[#0047FF] focus:outline-none"
          />
        </div>
      </div>

      <AdminTable headers={['Fecha', 'Empresa', 'Tipo', 'Concepto', 'Monto']}>
        {filtered.map((t) => {
          const amount = t.amount_mxn || 0;
          return (
            <tr key={t.id} className="border-b border-[#3F3F46] last:border-b-0">
              <td className="px-6 py-4 text-[#A1A1AA] text-sm whitespace-nowrap">
                {new Date(t.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
              </td>
              <td className="px-6 py-4 text-white font-medium">{t.company_name}</td>
              <td className="px-6 py-4">
                <span className="px-2 py-1 text-xs font-medium bg-[#3F3F46] text-white rounded-full">
                  {TX_TYPE_LABELS[t.type] || t.type}
                </span>
              </td>
              <td className="px-6 py-4 text-[#A1A1AA] text-sm max-w-xs truncate">{t.description}</td>
              <td className={`px-6 py-4 font-mono font-semibold whitespace-nowrap ${amount >= 0 && TX_POSITIVE.has(t.type) ? 'text-green-400' : amount < 0 ? 'text-red-400' : 'text-white'}`}>
                {TX_POSITIVE.has(t.type) && amount > 0 ? '+' : ''}${fmtMoney(amount)}
              </td>
            </tr>
          );
        })}
      </AdminTable>
      {filtered.length === 0 && (
        <p className="p-8 text-center text-[#A1A1AA] text-sm">{search ? `Sin resultados para "${search}"` : 'No hay movimientos'}</p>
      )}
    </div>
  );
}

// Admin Agents (global)
export function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null); // agent whose prompt is open

  useEffect(() => {
    (async () => {
      try {
        const res = await api.adminGetAgents();
        setAgents(res.data.agents || []);
      } catch {
        toast.error('Error al cargar agentes');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = agents.filter((a) =>
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-white">Agentes IA</h1>
          <p className="text-[#A1A1AA]">{agents.length} agentes en toda la plataforma</p>
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por agente o empresa..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#27272A] border border-[#3F3F46] rounded-xl text-white text-sm placeholder-[#52525B] focus:border-[#0047FF] focus:outline-none"
          />
        </div>
      </div>

      <AdminTable headers={['Agente', 'Empresa', 'Creado', 'Prompt']}>
        {filtered.map((a) => (
          <tr key={a.id} className="border-b border-[#3F3F46] last:border-b-0">
            <td className="px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#0047FF]/20 text-[#60A5FA] flex items-center justify-center">
                  <Robot size={18} />
                </div>
                <span className="text-white font-medium">{a.name}</span>
              </div>
            </td>
            <td className="px-6 py-4 text-[#A1A1AA]">{a.company_name}</td>
            <td className="px-6 py-4 text-[#A1A1AA] text-sm whitespace-nowrap">
              {new Date(a.created_at).toLocaleDateString('es-MX')}
            </td>
            <td className="px-6 py-4">
              <button
                onClick={() => setViewing(a)}
                className="px-3 py-1.5 text-xs font-medium bg-[#3F3F46] text-white rounded-xl hover:bg-[#52525B]"
              >
                Ver prompt
              </button>
            </td>
          </tr>
        ))}
      </AdminTable>
      {filtered.length === 0 && (
        <p className="p-8 text-center text-[#A1A1AA] text-sm">{search ? `Sin resultados para "${search}"` : 'No hay agentes'}</p>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="p-6 border-b border-[#3F3F46] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">{viewing.name}</h3>
                <p className="text-xs text-[#A1A1AA] mt-1">{viewing.company_name} · system prompt</p>
              </div>
              <button onClick={() => setViewing(null)} className="text-[#A1A1AA] hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <pre className="text-sm text-[#E4E4E7] whitespace-pre-wrap font-sans leading-relaxed">{viewing.system_prompt || 'Sin prompt'}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Admin Channels (global)
export function AdminChannels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.adminGetChannels();
        setChannels(res.data.channels || []);
      } catch {
        toast.error('Error al cargar canales');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = channels.filter((c) =>
    c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone_number?.includes(search) ||
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (c) => {
    if (c.sender_status === 'approved') return { label: 'Aprobado', cls: 'bg-green-500/20 text-green-400' };
    if (c.sender_status === 'pending') return { label: 'Pendiente', cls: 'bg-yellow-500/20 text-yellow-400' };
    if (c.sender_status === 'failed') return { label: 'Fallido', cls: 'bg-red-500/20 text-red-400' };
    if (c.is_verified) return { label: 'Verificado', cls: 'bg-green-500/20 text-green-400' };
    return { label: 'Sin verificar', cls: 'bg-[#3F3F46] text-[#A1A1AA]' };
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-white">Canales</h1>
          <p className="text-[#A1A1AA]">{channels.length} canales en toda la plataforma</p>
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por empresa o número..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#27272A] border border-[#3F3F46] rounded-xl text-white text-sm placeholder-[#52525B] focus:border-[#0047FF] focus:outline-none"
          />
        </div>
      </div>

      <AdminTable headers={['Empresa', 'Tipo', 'Número / Nombre', 'Estado', 'Conectado', 'Creado']}>
        {filtered.map((c) => {
          const badge = statusBadge(c);
          return (
            <tr key={c.id} className="border-b border-[#3F3F46] last:border-b-0">
              <td className="px-6 py-4 text-white font-medium">{c.company_name}</td>
              <td className="px-6 py-4 text-[#A1A1AA] capitalize">{c.channel_type === 'web' ? 'Web Chat' : 'WhatsApp'}</td>
              <td className="px-6 py-4 text-white font-mono text-sm">{c.phone_number || c.name || '—'}</td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
              </td>
              <td className="px-6 py-4">
                {c.is_connected ? (
                  <span className="text-green-400 text-sm font-medium">Sí</span>
                ) : (
                  <span className="text-[#52525B] text-sm">No</span>
                )}
              </td>
              <td className="px-6 py-4 text-[#A1A1AA] text-sm whitespace-nowrap">
                {c.created_at ? new Date(c.created_at).toLocaleDateString('es-MX') : '—'}
              </td>
            </tr>
          );
        })}
      </AdminTable>
      {filtered.length === 0 && (
        <p className="p-8 text-center text-[#A1A1AA] text-sm">{search ? `Sin resultados para "${search}"` : 'No hay canales'}</p>
      )}
    </div>
  );
}

// Admin Settings
export function AdminSettings() {
  const [settings, setSettings] = useState({
    default_ai_provider: 'openai',
    default_ai_model: 'gpt-4o-mini',
    openai_api_key: '',
    anthropic_api_key: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_number: '',
    twilio_sandbox_code: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('ai');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.adminGetSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that have been modified (not masked values)
      const dataToSend = {};
      Object.keys(settings).forEach(key => {
        const value = settings[key];
        if (value && !value.includes('***')) {
          dataToSend[key] = value;
        }
      });
      await api.adminUpdateSettings(dataToSend);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const providers = [
    { id: 'openai', name: 'OpenAI', models: ['gpt-4o-mini', 'gpt-4o', 'gpt-4'] },
    { id: 'anthropic', name: 'Anthropic', models: ['claude-sonnet-4-5-20250929', 'claude-haiku-4-5-20251001'] },
    { id: 'gemini', name: 'Google Gemini', models: ['gemini-3-flash-preview', 'gemini-2.5-pro'] }
  ];

  const currentProvider = providers.find(p => p.id === settings.default_ai_provider);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-white">
          Configuración Global
        </h1>
        <p className="text-[#A1A1AA]">Ajustes de la plataforma</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('ai')}
          className={`px-6 py-3 font-medium text-sm ${
            activeTab === 'ai' 
              ? 'bg-[#0047FF] text-white rounded-xl' 
              : 'bg-[#27272A] rounded-xl text-[#A1A1AA] hover:text-white'
          }`}
        >
          Modelo de IA
        </button>
        <button
          onClick={() => setActiveTab('twilio')}
          className={`px-6 py-3 font-medium text-sm ${
            activeTab === 'twilio' 
              ? 'bg-[#0047FF] text-white rounded-xl' 
              : 'bg-[#27272A] rounded-xl text-[#A1A1AA] hover:text-white'
          }`}
        >
          Twilio / WhatsApp
        </button>
        <button
          onClick={() => setActiveTab('keys')}
          className={`px-6 py-3 font-medium text-sm ${
            activeTab === 'keys' 
              ? 'bg-[#0047FF] text-white rounded-xl' 
              : 'bg-[#27272A] rounded-xl text-[#A1A1AA] hover:text-white'
          }`}
        >
          API Keys
        </button>
      </div>

      <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl max-w-2xl">
        {/* AI Settings Tab */}
        {activeTab === 'ai' && (
          <>
            <div className="p-6 border-b border-[#3F3F46]">
              <h3 className="font-bold text-white mb-2">Modelo de IA por defecto</h3>
              <p className="text-sm text-[#A1A1AA]">
                Este modelo será usado por todos los agentes de la plataforma
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Proveedor
                </label>
                <select
                  value={settings.default_ai_provider}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    default_ai_provider: e.target.value,
                    default_ai_model: providers.find(p => p.id === e.target.value)?.models[0] || ''
                  })}
                  data-testid="ai-provider-select"
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                >
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>{provider.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Modelo
                </label>
                <select
                  value={settings.default_ai_model}
                  onChange={(e) => setSettings({ ...settings, default_ai_model: e.target.value })}
                  data-testid="ai-model-select"
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                >
                  {currentProvider?.models.map((model) => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Twilio Settings Tab */}
        {activeTab === 'twilio' && (
          <>
            <div className="p-6 border-b border-[#3F3F46]">
              <h3 className="font-bold text-white mb-2">Configuración de Twilio</h3>
              <p className="text-sm text-[#A1A1AA]">
                Credenciales de Twilio para WhatsApp
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Account SID
                </label>
                <input
                  type="text"
                  value={settings.twilio_account_sid || ''}
                  onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Auth Token
                </label>
                <input
                  type="password"
                  value={settings.twilio_auth_token || ''}
                  onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                  placeholder="••••••••••••••••••••••••••••••••"
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
                <p className="text-xs text-[#52525B] mt-1">Deja vacío para mantener el valor actual</p>
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Número WhatsApp
                </label>
                <input
                  type="text"
                  value={settings.twilio_whatsapp_number || ''}
                  onChange={(e) => setSettings({ ...settings, twilio_whatsapp_number: e.target.value })}
                  placeholder="+14155238886"
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Código Sandbox
                </label>
                <input
                  type="text"
                  value={settings.twilio_sandbox_code || ''}
                  onChange={(e) => setSettings({ ...settings, twilio_sandbox_code: e.target.value })}
                  placeholder="join enter-particles"
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
                <p className="text-xs text-[#52525B] mt-1">Código que usuarios envían para activar el sandbox</p>
              </div>
            </div>
          </>
        )}

        {/* API Keys Tab */}
        {activeTab === 'keys' && (
          <>
            <div className="p-6 border-b border-[#3F3F46]">
              <h3 className="font-bold text-white mb-2">API Keys (Opcional)</h3>
              <p className="text-sm text-[#A1A1AA]">
                Configura tus API keys para los proveedores de IA.
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  value={settings.openai_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                  placeholder="sk-..."
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
                <p className="text-xs text-[#52525B] mt-1">Deja vacío para usar la key del servidor (.env)</p>
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#A1A1AA] mb-2">
                  Anthropic API Key
                </label>
                <input
                  type="password"
                  value={settings.anthropic_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, anthropic_api_key: e.target.value })}
                  placeholder="sk-ant-..."
                  className="w-full px-4 py-3 bg-[#09090B] border border-[#3F3F46] rounded-xl text-white focus:border-[#0047FF] focus:outline-none"
                />
                <p className="text-xs text-[#52525B] mt-1">Deja vacío para usar la key del servidor (.env)</p>
              </div>
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="p-6 border-t border-[#3F3F46]">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-settings"
            className="w-full bg-[#0047FF] text-white rounded-xl py-3 font-medium hover:bg-[#0035CC] transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : saved ? '¡Guardado!' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}
